import dayjs from 'dayjs'
import {BASE_PATH} from '@/constants'
import {APIError} from '@/constants/error'
import {DEBOUNCE_TIMEOUT} from '../constants/index'
import {IS_CLIENT, IS_DEV, IS_PROD, IS_SERVER, IS_TEST} from '../constants/env'
import getUaParam from './getUaParam'
import {materialReportLogin} from '@/service/video'
import {parse} from 'cookie'

type Mapped<T extends Record<string, any>, U extends Record<keyof T, string>> = { [P in keyof T as U[P]]: T[P] };

/**
 * @description: 将对象的key进行转换
 * @param obj: 原始对象
 * @param keyMap: 转换map
 * @return: 转换后的对象
 */
export const mapKeys = <T extends Record<string, any>, U extends Record<keyof T, string>>(obj: T, keyMap: U): Mapped<T, U> => {
    const result = {} as any
    for (const key in obj) {
        if (keyMap[key]) {
            result[keyMap[key]] = obj[key]
        }
    }
    return result as Mapped<T, U>
}
// const mapBoss = (obj: IBoosCategoryItem) => mapKeys(obj, {id: 'code', name: 'name'} as const);

/**
 * @description: 执行setTimeout
 * @param fn: 要执行的函数
 * @param delay: 延迟
 */
export const macroTask = (fn: () => void, delay = 0) => {
    const timeout = setTimeout(() => {
        clearTimeout(timeout)
        fn()
    }, delay)
}

/**
 * @description: 生成一个setTimeout的函数
 * @param fn: 要执行的函数
 * @param delay: 延迟
 */
export const genMacroTask = (fn: () => void, delay = 0) => () => {
    const timeout = setTimeout(() => {
        clearTimeout(timeout)
        fn()
    }, delay)
}

/**
 * @description: try catch捕获同步执行方法的错误
 * @param fn: 要执行的函数
 */
export const niceTry = <T>(fn: () => T): T | undefined => {
    try {
        return fn()
    }
    catch (e) {
        IS_CLIENT && console.log('e ===> ', e)
    }
}

/**
 * @description: try catch捕获promise的错误
 * @param promise: 要捕获的promise
 */
export const niceTryAsync = async <T>(promise: Promise<T>): Promise<[T, undefined] | [undefined, any]> => {
    try {
        const res = await promise
        return [res, undefined]
    }
    catch (e) {
        IS_CLIENT && console.log('e ===> ', e)
        return [undefined, e]
    }
}

/**
 * @description: 根据字节数获取文件大小
 * @param byte: 字节数
 * @param fractionDigits: 小数点位数
 * @param hasUnit: 是否有单位
 */
export const getFileSize = (byte: number, options = {fractionDigits: 2, hasUnit: true}) => {
    const {fractionDigits, hasUnit} = options
    const kb = byte / 1024
    const mb = kb / 1024
    const gb = mb / 1024
    let size = kb
    let unit = 'KB'
    if (mb > 1) {
        size = mb
        unit = 'M'
    }
    else if (gb > 1) {
        size = gb
        unit = 'G'
    }
    return `${size.toFixed(fractionDigits)}${hasUnit ? unit : ''}`
}

/**
 * @description: 获取分享到端外的链接
 */
export const getShareLink = () => `${location.href}&channel=browser`

/**
 * @description: 获取当前项目的其他页面的链接
 * @param path: 路径
 * @param other: query参数
 */
export const getRouterLink = (path: string, other: IObject = {}) => {
    if (typeof location === 'undefined') {
        return ''
    }
    const otherStr = Object.entries(other).reduce((pre, [key, value]) => {
        if (location.search.includes(key)) {
            return pre
        }
        return `${pre}${key}=${value}&`
    }, '')
    const search = `${location.search}${location.search ? '&' : '?'}${otherStr}`
    return `${location.origin}${BASE_PATH}${path.startsWith('/') ? '' : '/'}${path}${search}`
}

/**
 * @description: 判断当前版本是否>=指定版本
 * @param targetVersionStr: 指定版本的字符串 e.g. 4.6.5
 */
export const isVersionHigherThan = (targetVersionStr: string) => {
    const curVersionStr: string = getUaParam().version
    if (curVersionStr) {
        const targetVersionList = targetVersionStr.split('.').map((version) => parseInt(version))
        const curVersionList = curVersionStr.split('.').map((version) => parseInt(version))
        // eslint-disable-next-line guard-for-in
        for (const index in targetVersionList) {
            if (curVersionList[index] < targetVersionList[index]) {
                return false
            }
            if (curVersionList[index] > targetVersionList[index]) {
                return true
            }
        }
        return true
    }
    return false
}

export const isVersionHigherThanMulti = ({
    gaotuVersion,
    tutuVersion,
    jingpinVersion,
}: {gaotuVersion: string; tutuVersion: string; jingpinVersion: string}) => {
    const {
        isGaotuApp,
        isTutuApp,
        isJingpinApp,
    } = getUaParam()
    if (isGaotuApp) {
        return isVersionHigherThan(gaotuVersion)
    }
    if (isTutuApp) {
        return isVersionHigherThan(tutuVersion)
    }
    if (isJingpinApp) {
        return isVersionHigherThan(jingpinVersion)
    }
    return false
}

/**
 * @description: 防抖包裹函数
 * @param fn: 要执行的函数
 * @param wait: 防抖间隔
 */
export const deBounce: IWrapperFn = (fn, wait = DEBOUNCE_TIMEOUT) => {
    let timeout: any = null
    return (...args) => {
        clearTimeout(timeout)
        timeout = setTimeout(() => {
            fn(...args)
        }, wait)
    }
}

/**
 * @description: 立即执行防抖包裹函数
 * @param fn: 要执行的函数
 * @param wait: 防抖间隔
 */
export const deBounceImmediately: IWrapperFn = (fn, wait = DEBOUNCE_TIMEOUT) => {
    let timeout: any = null
    return (...args) => {
        if (timeout) {
            return
        }
        timeout = setTimeout(() => {
            clearTimeout(timeout)
            timeout = null
        }, wait)
        fn(...args)
    }
}

/**
 * @description: 将秒转为标注的显示格式
 * @param seconds: 秒数
 * @param type: 'symbol'(default) | 'CN' | 'minute'
 * @symbol: 23:33
 * @CN: 23分23秒
 * @minute: 83分23秒
 */
export const formatSeconds = (totalSeconds: number, type?: 'symbol' | 'CN' | 'minute') => {
    totalSeconds = Math.floor(totalSeconds)
    if (type === 'minute') {
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    const overOneHour = totalSeconds >= 60 * 60
    let format = overOneHour ? 'HH:mm:ss' : 'mm:ss'
    if (type === 'CN') {
        format = overOneHour ? 'HH时mm分ss秒' : 'mm分ss秒'
    }
    return dayjs()
        .startOf('day')
        .add(totalSeconds, 'second')
        .format(format)
}

/**
 * @description: 获取0开头的序列号
 * @param index: 当前是第几个
 * @param len: 总长度
 */
export const getSerialNumber = (index: number, len: number) => {
    if (len < 100) {
        if (index + 1 <= 9) {
            return `0${index + 1}`
        }
        if (index + 1 <= 99) {
            return index + 1
        }
    }
    if (index + 1 <= 9) {
        return `00${index + 1}`
    }
    if (index + 1 <= 99) {
        return `0${index + 1}`
    }
    return index + 1
}

/**
 * @description: 获取是否是竖屏
 * @return: 是否竖屏
 */
export const isVertical = () => (IS_CLIENT ? matchMedia('(orientation: portrait)').matches : true)

/**
 * @description: 根据value在map中找个一个符合的key
 */
export const findKey = <T, K extends string>(value: T, map: Record<K, T>): K => {
    for (const [k, v] of Object.entries(map)) {
        if (value === v) {
            return k as K
        }
    }
    return Object.entries(map)[0][0] as K
}

/**
 * @description: 等待一段时间
 * @param time: 等待的时间
 */
export const sleep = (time = 0) => new Promise((res) => macroTask(() => res(void 0), time))

/**
 * @description: 生成自定义错误的res
 * @param code: 错误码
 * @param reason: 错误原因
 * @param otherInfo: 其他信息
 */
export const genErrorRes = (other: IObject & {reason: string; otherInfo: any}, code = APIError.customError) => {
    return {
        props: {
            code,
            fallback: {},
            ...other,
        },
    }
}

/**
 * @description: 判断是否平板
 */
export const isTablet = () => {
    if (!window) return false
    // const userAgent = navigator.userAgent
    const {width, height} = window.screen
    const isTabletScreenSize = (width >= 600 && height >= 500)
    return isTabletScreenSize
}

/**
 * @description: 上报图书线索
 */
export const reprotBookClue = (bookId?: SN) => {
    const {isApp} = getUaParam()
    if (isApp) return
    const cookies = parse(document.cookie)
    // GID均存在过期情况，由后端进行校验 ， sid 以及 did在header中自动添加
    if (cookies?.GID) {
        materialReportLogin({bookId, qrcodeUrl: location?.href, gid: cookies?.GID})
    }
}

/**
 * @description: 重写根据图书内容接口数据，用于上报信息，bookId变为book_id ，grade传递id，subject传递id
 */
export const renameDataByBookInfo = (bookInfo: IObject) => {
    const {
        bookId,
        gradeList = [],
        subjectDTO,
        bookName,
        ...others
    } = bookInfo
    return {
        book_id: bookId,
        grade: gradeList?.map((item: IObject) => item.gradeName).join(','),
        subject: subjectDTO?.subjectId,
        book_name: bookName,
        ...others,
    }
}

// 根据当前域名获取需要发送的api地址
export const getApiUrl = () => {
    const host = location.host
    let hostList = host.split('.')
    if (IS_PROD) {
        hostList[0] = 'api'
    } else if (IS_DEV) {
        hostList = [
            'test-api',
            'gaotu',
            'cn',
        ]
    } else {
        hostList[0] = 'test-api'
    }
    return `https://${hostList.join('.')}`
}
